import type {
  StudyBriefAnalysisSelectDto,
  StudyP4E2ERowSelectDto,
  StudyRunCaseDashboardStageHeatmapRowDto,
  StudyValidationResultDto,
} from '@@types/api.types';
import { EffectDefinitionTooltip } from '@components/shared/effect-definition.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import { useGetEffects } from '@queries/effect.query';
import { CheckCircle2Icon, LineChartIcon } from 'lucide-react';
import { type CSSProperties, type FC, type ReactElement, type ReactNode, useMemo } from 'react';
import { buildEffectDefinitionByName, normalizeEffectDefinitionKey } from './meia-effect-definitions.util';
import { BRIEF_TABLE_ROW_LIMIT, formatBriefEnumLabel, formatBriefValidationMessage } from './meia-p5-brief.util';

export type BriefAnalysisTab = 'cost' | 'sensitivity' | 'traceability' | 'validation';

type BriefAnalysisTileProps = {
  activeTab: BriefAnalysisTab;
  activeThreadId: string | null;
  briefAnalysis?: StudyBriefAnalysisSelectDto;
  /** Distinguishes a failed analysis fetch from "no analysis yet" — the query never toasts. */
  hasAnalysisError: boolean;
  isBriefAnalysisLoading: boolean;
  isValidationLoading: boolean;
  onSelectTab: (tab: BriefAnalysisTab) => void;
  rows: StudyP4E2ERowSelectDto[];
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
  threadLabelsById?: Map<string, string>;
  validationResults: StudyValidationResultDto[];
};

export const BriefAnalysisTile: FC<BriefAnalysisTileProps> = (props) => {
  const {
    activeTab,
    activeThreadId,
    briefAnalysis,
    hasAnalysisError,
    isBriefAnalysisLoading,
    isValidationLoading,
    onSelectTab,
    rows,
    stages,
    threadLabelsById,
    validationResults,
  } = props;

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        title: (
          <span className="inline-flex items-center gap-2">
            <LineChartIcon className="size-4 text-primary" />
            Analysis
          </span>
        ),
      }}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200">
        {analysisTabs.map((tab) => (
          <button
            className={cn(
              '-mb-px h-9 border-b-2 px-3 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'border-primary text-gray-950'
                : 'border-transparent text-gray-500 hover:text-gray-900',
            )}
            key={tab.id}
            onClick={() => onSelectTab(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'traceability' && (
        <BriefTraceabilityPanel
          activeThreadId={activeThreadId}
          rows={rows}
          stages={stages}
          threadLabelsById={threadLabelsById}
        />
      )}
      {activeTab === 'cost' && (
        <BriefCostEffectivenessPanel
          analysis={briefAnalysis}
          hasError={hasAnalysisError}
          isLoading={isBriefAnalysisLoading}
        />
      )}
      {activeTab === 'sensitivity' && (
        <BriefSensitivityPanel
          analysis={briefAnalysis}
          hasError={hasAnalysisError}
          isLoading={isBriefAnalysisLoading}
        />
      )}
      {activeTab === 'validation' && (
        <BriefValidationPanel
          isLoading={isValidationLoading}
          validationResults={validationResults}
        />
      )}
    </Tile>
  );
};

type BriefTraceabilityPanelProps = {
  activeThreadId: string | null;
  rows: StudyP4E2ERowSelectDto[];
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
  threadLabelsById?: Map<string, string>;
};

const BriefTraceabilityPanel: FC<BriefTraceabilityPanelProps> = (props) => {
  const { activeThreadId, rows, stages, threadLabelsById } = props;
  const activeRows = activeThreadId
    ? rows.filter((row) => row.threadId === activeThreadId).slice(0, BRIEF_TABLE_ROW_LIMIT)
    : rows.slice(0, BRIEF_TABLE_ROW_LIMIT);
  const stageNameByName = new Set(stages.map((stage) => stage.stageName));

  return (
    <>
      {activeRows.length === 0 ? (
        <p className="text-xs text-gray-500">No P4 task-technology trace rows are available for the selected thread.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-gray-200 bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Task</th>
                <th className="px-3 py-2 font-medium">Technology</th>
                {!activeThreadId && <th className="px-3 py-2 font-medium">Thread</th>}
                <th className="px-3 py-2 font-medium">Target</th>
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => (
                <tr
                  className="border-b border-gray-100 last:border-b-0"
                  key={row.id}
                >
                  <td className="px-3 py-2">
                    <span
                      className={cn(stageNameByName.has(row.swimlanePhaseName) ? 'text-gray-800' : 'text-gray-500')}
                    >
                      {row.swimlanePhaseName}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-800">{row.taskName}</td>
                  <td className="px-3 py-2 text-gray-800">{row.technologyName}</td>
                  {!activeThreadId && (
                    <td className="max-w-64 px-3 py-2 text-gray-600">
                      <div
                        className="truncate"
                        title={threadLabelsById?.get(row.threadId) ?? row.threadName}
                      >
                        {threadLabelsById?.get(row.threadId) ?? row.threadName}
                      </div>
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-500">
                    {row.expectedThreatTargetSystemCode ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

type BriefCostRow = StudyBriefAnalysisSelectDto['costRows'][number];
type BriefSensitivityParameter = StudyBriefAnalysisSelectDto['sensitivitySummary']['parameters'][number];
type BriefSensitivityTransition = StudyBriefAnalysisSelectDto['sensitivitySummary']['transitions'][number];
type BriefSensitivitySweepPoint = {
  parameterValue: number;
  responseScore: number;
  x: number;
  y: number;
};
type BriefSensitivitySweepScale = {
  maxScore: number;
  minScore: number;
};
type BriefSensitivityParameterMeta = {
  inputLabel: string;
  inputUnit: string;
  outputLabel: string;
  outputUnit: string;
};

type BriefComputedAnalysisPanelProps = {
  analysis?: StudyBriefAnalysisSelectDto;
  hasError: boolean;
  isLoading: boolean;
};

const BriefCostEffectivenessPanel: FC<BriefComputedAnalysisPanelProps> = (props) => {
  const { analysis, hasError, isLoading } = props;
  const { data: effects = [] } = useGetEffects();
  const effectDefinitionByName = useMemo(() => buildEffectDefinitionByName(effects), [effects]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (hasError) {
    return (
      <BriefAnalysisEmptyPanel
        detail="The analysis request failed. Switch run case or mission and back to retry."
        title="Analysis failed to load"
      />
    );
  }

  if (!analysis) {
    return (
      <BriefAnalysisEmptyPanel
        detail="Select a scored run case before cost analysis can be computed."
        title="Cost analysis unavailable"
      />
    );
  }

  if (analysis.costRows.length === 0) {
    return (
      <BriefAnalysisEmptyPanel
        detail="No task technology assignments are available in this Brief scope."
        title="No assigned technologies"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <BriefCostSummaryGrid analysis={analysis} />
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full text-left text-xs">
          <thead className="border-b border-gray-200 bg-gray-50 text-gray-500">
            <tr>
              <th className="px-3 py-2 font-medium">Technology</th>
              <th className="px-3 py-2 font-medium">Campaign Cost</th>
              <th className="px-3 py-2 font-medium">Task Score</th>
              <th className="px-3 py-2 font-medium">Value / $1M</th>
              <th className="px-3 py-2 font-medium">Effects</th>
            </tr>
          </thead>
          <tbody>
            {analysis.costRows.slice(0, BRIEF_TABLE_ROW_LIMIT).map((row) => (
              <BriefCostRowEntry
                effectDefinitionByName={effectDefinitionByName}
                key={row.technologyId}
                row={row}
              />
            ))}
          </tbody>
        </table>
      </div>
      {analysis.costSummary.missingCostTechnologyCount > 0 && (
        <p className="text-xs text-gray-500">
          {analysis.costSummary.missingCostTechnologyCount} assigned technologies have no active component prices.
        </p>
      )}
    </div>
  );
};

const BriefSensitivityPanel: FC<BriefComputedAnalysisPanelProps> = (props) => {
  const { analysis, hasError, isLoading } = props;

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  if (hasError) {
    return (
      <BriefAnalysisEmptyPanel
        detail="The analysis request failed. Switch run case or mission and back to retry."
        title="Analysis failed to load"
      />
    );
  }

  if (!analysis) {
    return (
      <BriefAnalysisEmptyPanel
        detail="Select a scored run case before sensitivity analysis can be computed."
        title="Sensitivity unavailable"
      />
    );
  }

  const { parameters, transitions } = analysis.sensitivitySummary;
  const hasSensitivityRows = parameters.length > 0 || transitions.length > 0;

  if (!hasSensitivityRows) {
    return (
      <BriefAnalysisEmptyPanel
        detail="The selected scope has no sensitivity movement for the default OAT sweep."
        title="No sensitivity movement"
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-gray-500">
        Which inputs most affect the selected run output, and where verdicts move when one input is swept while the rest
        remain fixed.
      </p>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <BriefSensitivityInfluencePanel parameters={parameters} />
        <BriefSensitivitySweepPanel
          parameters={parameters}
          transitions={transitions}
        />
      </div>
    </div>
  );
};

const BriefCostRowEntry: FC<{ effectDefinitionByName: Map<string, string>; row: BriefCostRow }> = (props) => {
  const { effectDefinitionByName, row } = props;

  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="px-3 py-2 text-gray-800">{row.technologyName}</td>
      <td className="px-3 py-2 text-gray-700">{row.missingCost ? '-' : formatBriefCurrency(row.totalCost)}</td>
      <td className="px-3 py-2 text-gray-700">{formatBriefScore(row.effectivenessScore)}</td>
      <td className="px-3 py-2 text-gray-700">{formatBriefValue(row.valuePerMillion)}</td>
      <td className="px-3 py-2 text-gray-600">
        <BriefEffectsCell
          effectDefinitionByName={effectDefinitionByName}
          row={row}
        />
      </td>
    </tr>
  );
};

const BriefEffectsCell: FC<{ effectDefinitionByName: Map<string, string>; row: BriefCostRow }> = (props) => {
  const { effectDefinitionByName, row } = props;
  if (row.effectTypes.length === 0) return <>-</>;

  const visibleEffects = row.effectTypes.slice(0, 2);
  const remainingCount = row.effectTypes.length - 2;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleEffects.map((effectType) => {
        const label = formatBriefEnumLabel(effectType);
        const definition = effectDefinitionByName.get(normalizeEffectDefinitionKey(label));
        const badge = (
          <Badge
            className={cn(definition && 'cursor-help')}
            variant="primary"
          >
            {label}
          </Badge>
        );

        return (
          <EffectDefinitionTooltip
            definition={definition}
            key={effectType}
            trigger={badge}
          />
        );
      })}
      {remainingCount > 0 && <Badge variant="primary">+{remainingCount}</Badge>}
    </div>
  );
};

const BriefCostSummaryGrid: FC<{ analysis: StudyBriefAnalysisSelectDto }> = (props) => {
  const { analysis } = props;
  const { costSummary } = analysis;
  const stats = [
    { label: 'Campaign Total', value: formatBriefCurrency(costSummary.grandTotal) },
    {
      label: 'Priced Technologies',
      value: `${costSummary.pricedTechnologyCount}/${costSummary.technologyCount}`,
    },
    { label: 'Missing Costs', value: String(costSummary.missingCostTechnologyCount) },
    { label: 'Best Value', value: costSummary.bestTechnologyName ?? '-' },
  ];

  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
      {stats.map((stat) => (
        <div
          className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
          key={stat.label}
        >
          <div className="text-[11px] font-medium uppercase text-gray-400">{stat.label}</div>
          <div
            className="mt-1 truncate text-sm font-semibold text-gray-900"
            title={stat.value}
          >
            {stat.value}
          </div>
          {stat.label === 'Best Value' &&
            costSummary.bestValuePerMillion !== null &&
            costSummary.bestValuePerMillion !== undefined && (
              <div className="mt-0.5 text-[11px] text-gray-500">
                {formatBriefValue(costSummary.bestValuePerMillion)} per $1M
              </div>
            )}
        </div>
      ))}
    </div>
  );
};

const BriefSensitivityInfluencePanel: FC<{ parameters: BriefSensitivityParameter[] }> = (props) => {
  const { parameters } = props;
  const maxImpactScore = Math.max(...parameters.map((parameter) => Math.abs(parameter.impactScore)), 0);

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-semibold text-gray-900">Input influence</div>
        <div className="text-[11px] text-gray-500">sorted by influence</div>
      </div>
      <BriefSensitivitySection
        emptyText="No parameter ranking available."
        rows={parameters}
      >
        {(parameter) => (
          <BriefSensitivityInfluenceRow
            key={parameter.parameter}
            maxImpactScore={maxImpactScore}
            parameter={parameter}
          />
        )}
      </BriefSensitivitySection>
      <div className="mt-3 flex items-center gap-3 border-t border-gray-200 pt-2 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm bg-primary/70" />
          Raises response
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm bg-amber-500/70" />
          Lowers response
        </span>
      </div>
    </div>
  );
};

const BriefSensitivityInfluenceRow: FC<{
  maxImpactScore: number;
  parameter: BriefSensitivityParameter;
}> = (props) => {
  const { maxImpactScore, parameter } = props;
  const meta = getBriefSensitivityParameterMeta(parameter.parameter);
  const isPositive = parameter.impactScore >= 0;

  return (
    <div className="text-xs">
      <div className="mb-1 flex items-center justify-between gap-3">
        <span
          className="truncate font-medium text-gray-900"
          title={`${meta.inputLabel} (${meta.inputUnit})`}
        >
          {parameter.rank}. {meta.inputLabel} ({meta.inputUnit})
        </span>
        <span
          className="shrink-0 truncate text-right text-[11px] text-gray-500"
          title={`${meta.outputLabel} (${meta.outputUnit})`}
        >
          {meta.outputLabel} ({meta.outputUnit})
        </span>
      </div>
      <div
        className="relative h-4 overflow-hidden rounded bg-gray-50"
        title={formatBriefSensitivityImpactTitle({ meta, parameter })}
      >
        <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300" />
        <div
          className={cn('absolute inset-y-0 rounded', isPositive ? 'bg-primary/70' : 'bg-amber-500/70')}
          style={toInfluenceBarStyle({ maxImpactScore, parameter })}
        />
        <div className="absolute inset-0 flex items-center justify-end pr-2">
          <span className={cn('text-[10px] font-semibold', isPositive ? 'text-primary' : 'text-amber-700')}>
            {isPositive ? '+' : ''}
            {formatBriefNumber(parameter.impactScore)} {meta.outputUnit} / sweep
          </span>
        </div>
      </div>
    </div>
  );
};

type BriefSensitivitySectionProps<T> = {
  children: (row: T) => ReactNode;
  emptyText: string;
  rows: T[];
};

const BriefSensitivitySection = <T,>(props: BriefSensitivitySectionProps<T>): ReactElement => {
  const { children, emptyText, rows } = props;

  return (
    <div>
      {rows.length === 0 ? (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-gray-500">
          {emptyText}
        </div>
      ) : (
        <div className="space-y-2">{rows.slice(0, BRIEF_TABLE_ROW_LIMIT).map(children)}</div>
      )}
    </div>
  );
};

const BriefSensitivitySweepPanel: FC<{
  parameters: BriefSensitivityParameter[];
  transitions: BriefSensitivityTransition[];
}> = (props) => {
  const { parameters, transitions } = props;
  const selectedParameter = parameters[0] ?? null;
  const selectedParameterMeta = selectedParameter
    ? getBriefSensitivityParameterMeta(selectedParameter.parameter)
    : getBriefSensitivityParameterMeta(null);
  const sweepPoints = toSweepPoints({ parameter: selectedParameter });
  const sweepScale = toSweepScale({ parameter: selectedParameter });

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-semibold text-gray-900">One-at-a-time sweep</div>
      </div>
      <div className="mb-2 text-xs text-gray-600">
        <span className="font-medium">{selectedParameterMeta.inputLabel}</span>
        <span className="text-gray-500"> - </span>
        <span>{selectedParameterMeta.outputLabel}</span>
      </div>
      <svg
        aria-label="One-at-a-time sensitivity sweep"
        className="h-44 w-full text-gray-300"
        role="img"
        viewBox="0 0 300 170"
      >
        <line
          stroke="currentColor"
          x1="38"
          x2="38"
          y1="10"
          y2="130"
        />
        <line
          stroke="currentColor"
          x1="38"
          x2="290"
          y1="130"
          y2="130"
        />
        <polyline
          className="text-primary"
          fill="none"
          points={toSweepPolylinePoints({ points: sweepPoints })}
          stroke="currentColor"
          strokeWidth="1.5"
        />
        {sweepPoints.map((point) => (
          <circle
            className="text-primary"
            cx={point.x}
            cy={point.y}
            fill="currentColor"
            key={`${point.parameterValue}-${point.responseScore}`}
            r="2.5"
          />
        ))}
        <text
          fill="currentColor"
          fontSize="9"
          textAnchor="end"
          x="33"
          y="14"
        >
          {formatBriefNumber(sweepScale.maxScore)}
        </text>
        <text
          fill="currentColor"
          fontSize="9"
          textAnchor="end"
          x="33"
          y="133"
        >
          {formatBriefNumber(sweepScale.minScore)}
        </text>
        {sweepPoints[0] && (
          <text
            className="text-gray-500"
            fill="currentColor"
            fontSize="9"
            textAnchor="middle"
            x="38"
            y="145"
          >
            {formatBriefNumber(sweepPoints[0].parameterValue)}
          </text>
        )}
        {sweepPoints[sweepPoints.length - 1] && (
          <text
            className="text-gray-500"
            fill="currentColor"
            fontSize="9"
            textAnchor="end"
            x="290"
            y="145"
          >
            {formatBriefNumber(sweepPoints[sweepPoints.length - 1].parameterValue)}
          </text>
        )}
        <text
          className="text-gray-500"
          fill="currentColor"
          fontSize="9"
          textAnchor="middle"
          x="164"
          y="162"
        >
          {selectedParameterMeta.inputLabel} ({selectedParameterMeta.inputUnit})
        </text>
      </svg>
      {transitions.length === 0 ? (
        <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          Verdict remained stable across the default sweep.
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          {transitions.slice(0, 4).map((transition) => (
            <div
              className="flex items-start justify-between gap-3 rounded border border-gray-200 px-3 py-2 text-xs"
              key={`${transition.missionId}-${transition.parameter}-${transition.atValue}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{transition.missionName}</div>
                <div className="mt-0.5 text-gray-500">
                  {formatBriefEnumLabel(transition.parameter)} at {formatBriefNumber(transition.atValue)}
                </div>
              </div>
              <Badge
                className="shrink-0"
                variant="outline"
              >
                {formatBandTransition({ fromBand: transition.fromBand, toBand: transition.toBand })}
              </Badge>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 text-[11px] text-gray-500">
        Baseline: <span className="font-medium text-gray-700">current run-case {selectedParameterMeta.inputUnit}</span>
      </div>
    </div>
  );
};

type BriefValidationPanelProps = {
  isLoading: boolean;
  validationResults: StudyValidationResultDto[];
};

const BriefValidationPanel: FC<BriefValidationPanelProps> = (props) => {
  const { isLoading, validationResults } = props;

  if (isLoading) return <Skeleton className="h-28 w-full" />;

  if (validationResults.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-500">
        <CheckCircle2Icon className="size-4 text-emerald-600" />
        No validation findings.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-gray-200">
      {validationResults.slice(0, BRIEF_TABLE_ROW_LIMIT).map((result) => (
        <div
          className="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2 text-xs last:border-b-0"
          key={`${result.ruleId}-${result.entityId}`}
        >
          <div>
            <div className="font-medium text-gray-900">{formatBriefValidationMessage(result.message)}</div>
            <div className="font-mono text-[10px] text-gray-400">
              {result.ruleId} · {formatBriefEnumLabel(result.entityType)}
            </div>
          </div>
          <Badge variant={result.blocking ? 'destructive' : 'warning'}>{formatBriefEnumLabel(result.severity)}</Badge>
        </div>
      ))}
    </div>
  );
};

type BriefAnalysisEmptyPanelProps = {
  detail: string;
  title: string;
};

const BriefAnalysisEmptyPanel: FC<BriefAnalysisEmptyPanelProps> = (props) => {
  const { detail, title } = props;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-5 text-center text-xs text-gray-500">
      <div className="font-medium text-gray-800">{title}</div>
      <p className="mt-1">{detail}</p>
    </div>
  );
};

const formatBandTransition = (params: { fromBand: string; toBand: string }): string =>
  `${formatBriefEnumLabel(params.fromBand)} -> ${formatBriefEnumLabel(params.toBand)}`;

const formatBriefCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 0,
    style: 'currency',
  }).format(value);

const formatBriefNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);

const formatBriefSensitivityImpactTitle = (params: {
  meta: BriefSensitivityParameterMeta;
  parameter: BriefSensitivityParameter;
}): string => {
  const direction = params.parameter.impactScore >= 0 ? 'higher' : 'lower';
  const value = `${params.parameter.impactScore >= 0 ? '+' : ''}${formatBriefNumber(params.parameter.impactScore)}`;
  return `Sweeping ${params.meta.inputLabel.toLowerCase()} across the default range moves ${params.meta.outputLabel.toLowerCase()} ${direction} by ${value} ${params.meta.outputUnit}.`;
};

const formatBriefScore = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';
  return `${formatBriefNumber(value)}/100`;
};

const formatBriefValue = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';
  return formatBriefNumber(value);
};

const toInfluenceBarStyle = (params: {
  maxImpactScore: number;
  parameter: BriefSensitivityParameter;
}): CSSProperties => {
  const absoluteScore = Math.abs(params.parameter.impactScore);
  const width = params.maxImpactScore <= 0 ? 0 : Math.max(6, (absoluteScore / params.maxImpactScore) * 48);
  if (params.parameter.impactScore >= 0) {
    return {
      left: '50%',
      width: `${width}%`,
    };
  }
  return {
    right: '50%',
    width: `${width}%`,
  };
};

const toSweepPoints = (params: {
  parameter: BriefSensitivityParameter | null;
}): BriefSensitivitySweepPoint[] => {
  const samples = params.parameter?.samples ?? [];
  if (samples.length === 0) return [];
  const sweepScale = toSweepScale(params);
  const scoreRange = Math.max(sweepScale.maxScore - sweepScale.minScore, 1);

  return samples.map((sample, sampleIndex) => {
    const x = samples.length === 1 ? 164 : 38 + (sampleIndex / (samples.length - 1)) * 252;
    const yValue = (sample.responseScore - sweepScale.minScore) / scoreRange;
    return {
      parameterValue: sample.parameterValue,
      responseScore: sample.responseScore,
      x,
      y: 130 - yValue * 110,
    };
  });
};

const toSweepPolylinePoints = (params: { points: BriefSensitivitySweepPoint[] }): string => {
  return params.points.map((point) => `${point.x},${point.y}`).join(' ');
};

const toSweepScale = (params: { parameter: BriefSensitivityParameter | null }): BriefSensitivitySweepScale => {
  const scores = params.parameter?.samples.map((sample) => sample.responseScore) ?? [];
  if (scores.length === 0) return { maxScore: 100, minScore: 0 };
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  if (minScore === maxScore) {
    return {
      maxScore: maxScore + 1,
      minScore: minScore - 1,
    };
  }
  const padding = (maxScore - minScore) * 0.08;
  return {
    maxScore: maxScore + padding,
    minScore: minScore - padding,
  };
};

const getBriefSensitivityParameterMeta = (parameter: string | null): BriefSensitivityParameterMeta => {
  switch (parameter) {
    case 'c2LatencyFactor':
      return {
        inputLabel: 'C2 latency factor',
        inputUnit: '0-1',
        outputLabel: 'Mission response',
        outputUnit: 'pts',
      };
    case 'cyberPressure':
      return {
        inputLabel: 'Cyber pressure',
        inputUnit: '0-1',
        outputLabel: 'Mission response',
        outputUnit: 'pts',
      };
    case 'environmentalFriction':
      return {
        inputLabel: 'Environmental friction',
        inputUnit: '0-1',
        outputLabel: 'Mission response',
        outputUnit: 'pts',
      };
    case 'threatDifficulty':
      return {
        inputLabel: 'Threat difficulty',
        inputUnit: '0-1',
        outputLabel: 'Mission response',
        outputUnit: 'pts',
      };
    default:
      return {
        inputLabel: parameter ? formatBriefEnumLabel(parameter) : 'Selected input',
        inputUnit: '0-1',
        outputLabel: 'Mission response',
        outputUnit: 'pts',
      };
  }
};

const analysisTabs: { id: BriefAnalysisTab; label: string }[] = [
  { id: 'traceability', label: 'Traceability' },
  { id: 'cost', label: 'Cost-effectiveness' },
  { id: 'sensitivity', label: 'Sensitivity' },
  { id: 'validation', label: 'Validation' },
];
